import type { TradeBriefingTradeType } from '../types';
import { normalizeEmail } from './emailMatching';

const TRADE_TYPES: TradeBriefingTradeType[] = [
  'finishing',
  'millwork',
  'electrical',
  'roofing',
  'general',
  'other',
];

export type TradeCompanySource = {
  id?: string;
  dealId?: string;
  companyName?: string | null;
  contactName?: string | null;
  inviteEmail?: string | null;
  invitedUid?: string | null;
  tradeType?: TradeBriefingTradeType | string;
  revokedAt?: unknown;
};

export type TradeCompanyOption = {
  key: string;
  companyName: string;
  contactName: string;
  inviteEmail: string;
  invitedUid: string;
  tradeType: TradeBriefingTradeType;
  projectCount: number;
  dealIds: string[];
};

export function normalizeTradeCompanyName(value: unknown): string {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
}

export function tradeCompanyIdentityKey(link: TradeCompanySource): string {
  const uid = typeof link.invitedUid === 'string' ? link.invitedUid.trim() : '';
  if (uid) return `uid:${uid}`;
  const email = typeof link.inviteEmail === 'string' ? normalizeEmail(link.inviteEmail) : '';
  if (email) return `email:${email}`;
  const name = normalizeTradeCompanyName(link.companyName).toLowerCase();
  if (name) return `name:${name}`;
  return '';
}

function asTradeType(value: unknown): TradeBriefingTradeType {
  return TRADE_TYPES.includes(value as TradeBriefingTradeType)
    ? (value as TradeBriefingTradeType)
    : 'general';
}

export function buildTradeCompanyOptions(links: TradeCompanySource[]): TradeCompanyOption[] {
  const map = new Map<string, TradeCompanyOption & { dealSet: Set<string> }>();

  for (const link of links) {
    const key = tradeCompanyIdentityKey(link);
    if (!key) continue;

    const companyName = normalizeTradeCompanyName(link.companyName);
    const contactName = typeof link.contactName === 'string' ? link.contactName.trim() : '';
    const inviteEmail = typeof link.inviteEmail === 'string' ? normalizeEmail(link.inviteEmail) : '';
    const invitedUid = typeof link.invitedUid === 'string' ? link.invitedUid.trim() : '';
    const tradeType = asTradeType(link.tradeType);
    const dealId = typeof link.dealId === 'string' ? link.dealId.trim() : '';
    const revoked = Boolean(link.revokedAt);

    let row = map.get(key);
    if (!row) {
      row = {
        key,
        companyName: companyName || inviteEmail || 'Trade',
        contactName,
        inviteEmail,
        invitedUid,
        tradeType,
        projectCount: 0,
        dealIds: [],
        dealSet: new Set(),
      };
      map.set(key, row);
    } else {
      if (!row.companyName && companyName) row.companyName = companyName;
      if (!row.contactName && contactName) row.contactName = contactName;
      if (!row.inviteEmail && inviteEmail) row.inviteEmail = inviteEmail;
      if (!row.invitedUid && invitedUid) row.invitedUid = invitedUid;
      if (row.tradeType === 'general' && tradeType !== 'general') row.tradeType = tradeType;
    }
    if (!revoked && dealId) row.dealSet.add(dealId);
  }

  return Array.from(map.values())
    .map(({ dealSet, ...rest }) => ({
      ...rest,
      dealIds: Array.from(dealSet),
      projectCount: dealSet.size,
    }))
    .sort((a, b) => a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' }));
}

export function findDealLinkForCompany<T extends TradeCompanySource>(
  links: T[],
  companyKey: string,
): T | undefined {
  if (!companyKey) return undefined;
  return links.find(link => !link.revokedAt && tradeCompanyIdentityKey(link) === companyKey);
}
